import { useEffect, useRef } from 'react'
import { useRouter } from 'expo-router'

type LobbyForNav =
  | {
      state: string
      winnerName?: string | null
    }
  | null
  | undefined

type Params = {
  tournamentId: string | undefined
  lobby: LobbyForNav
  /** `justFinished=1` — show a "Tournament complete" toast on first observe. */
  justFinished: string | undefined
}

/**
 * Lobby side-effects driven by URL params.
 *
 * Table-move navigation no longer routes through the lobby — the game
 * screen redirects directly to the new game route once the server has
 * reassigned the player. See `useRemovedFromGame` in `GameModals.tsx`.
 *
 * This hook now only clears the `justFinished` URL param after it has
 * served its purpose (suppressing a duplicate winner announcement — the
 * in-game game-over modal already announced it, so the lobby's
 * `TournamentWinnerPrompt` skips its repeat).
 */
export function useTournamentLobbyNavigation({ tournamentId, lobby, justFinished }: Params) {
  const router = useRouter()
  const justFinishedShownRef = useRef<string | null>(null)

  useEffect(() => {
    justFinishedShownRef.current = null
  }, [tournamentId])

  useEffect(() => {
    if (!tournamentId || !lobby) return

    // The winner announcement modal (`TournamentWinnerPrompt`, mounted at the
    // MTT route layout) has replaced the old "Tournament complete" toast. The
    // `justFinished` param now only tells the layout modal that the in-game
    // game-over modal already announced the winner — skip the repeat there.
    // Clear the param so a later lobby visit announces normally.
    if (justFinished === '1' && justFinishedShownRef.current !== tournamentId) {
      justFinishedShownRef.current = tournamentId
      router.setParams({ justFinished: undefined })
    }
  }, [tournamentId, lobby, justFinished, router])
}
